import { Box, BoxProps, Typography } from "@mui/material";
import {
  formatDate,
  formatMonthYear,
  formatPercent,
  formatPercentFactor,
} from "@app/utils";
import {
  CarSimulationPercentileColumn,
  CarSimulationPercentileRow,
  CarSimulationPercentileTable,
  CarSimulationPercentileValue,
} from "./useSimulationReturns";
import { chartsColor } from "@app/theme";
import { SimulationModelsHistoryItem } from "./useSimulationModelsHistory";

const formatValue = (value?: number | null) =>
  value
    ? formatPercentFactor(value, {
        decimalPlaces: 2,
        forceShowDecimals: true,
      })
    : "-";

interface LabelHeaderProps {
  sx?: BoxProps["sx"];
}

const LabelHeader = (props: LabelHeaderProps) => {
  return (
    <Box
      sx={{
        marginLeft: "-1px",
        border: 1,
        borderColor: "table.border",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "table.background.header",
        "&:last-of-type": {
          borderRightWidth: 1,
        },
        "& > *": {
          borderBottom: 1,
          borderBottomColor: "table.border",
          "&:last-of-type": {
            borderBottom: "none",
            backgroundColor: "white",
          },
        },
        ...props.sx,
      }}
    >
      <Box
        sx={{
          height: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      ></Box>
      <Box sx={{ flex: "auto" }} />
    </Box>
  );
};

const getGridTemplateColumns = (columnCount: number) =>
  `0.5fr repeat(${columnCount}, 1fr)`;

interface ColumnHeaderProps {
  sx?: BoxProps["sx"];
  label: string;
  hiddenFields: string[];
}

const ColumnHeader = (props: ColumnHeaderProps) => {
  return (
    <Box
      sx={{
        marginLeft: "-1px",
        border: 1,
        borderColor: "table.border",
        borderLeftWidth: 2,
        borderRightWidth: 2,
        backgroundColor: "table.background.header",
        "&:last-of-type": {
          borderRightWidth: 1,
        },
        "& > *": {
          borderBottom: 1,
          borderBottomColor: "table.border",
          "&:last-of-type": {
            borderBottom: "none",
          },
        },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        ...props.sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          px: 1,
          py: 2,
          height: 60,
          width: "100%",
        }}
      >
        <Typography variant="h6SSemiBold">{props.label}</Typography>
      </Box>
      <Box
        sx={{
          flex: "auto",
          display: "grid",
          gridTemplateColumns: `repeat(${props.hiddenFields.length > 0 ? 1 : 2}, 1fr)`,
          "& > *": {
            backgroundColor: "white",
            borderStyle: "solid",
            borderColor: "table.border",
            borderWidth: 0,
            borderLeftWidth: 1,
            "&:first-of-type": {
              borderLeftWidth: 0,
            },
            py: 1.5,
            px: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "center",
          },
        }}
      >
        {!props.hiddenFields.includes("mu") && (
          <Typography variant="par02Regular">Mu</Typography>
        )}
        {!props.hiddenFields.includes("sigma") && (
          <Typography variant="par02Regular">Sigma</Typography>
        )}
      </Box>
    </Box>
  );
};

interface RowProps {
  sx?: BoxProps["sx"];
  item: SimulationModelsHistoryItem;
  hiddenFields: string[];
}

const Row = (props: RowProps) => {
  return (
    <Box
      sx={{
        display: "grid",
        minHeight: 60,
        gridTemplateColumns: "1fr",
        ...props.sx,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: getGridTemplateColumns(2),
          ".row-cell": {
            marginTop: "-1px",
            marginLeft: "-1px",
            border: 1,
            borderColor: "table.border",
            borderLeftWidth: 2,
            borderRightWidth: 2,
            "&:last-of-type": {
              borderRightWidth: 1,
            },

            display: "grid",
            gridTemplateColumns: `repeat(${props.hiddenFields.length > 0 ? 1 : 2}, 1fr)`,

            "& > *": {
              borderStyle: "solid",
              borderColor: "table.border",
              borderWidth: 0,
              borderLeftWidth: 1,
              "&:first-of-type": {
                borderLeftWidth: 0,
              },

              overflow: "hidden",
              textOverflow: "ellipsis",

              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              "& > *": {
                maxWidth: 54,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            marginTop: "-1px",
            marginLeft: "-1px",
            border: 1,
            borderColor: "table.border",
            p: 1.5,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <Typography variant="par02Regular">
            {formatMonthYear(props.item.date)}
          </Typography>
        </Box>
        <Box className="row-cell">
          {!props.hiddenFields.includes("mu") && (
            <Typography variant="par02Regular">
              {formatValue(props.item.staticMu)}
            </Typography>
          )}
          {!props.hiddenFields.includes("sigma") && (
            <Typography variant="par02Regular">
              {formatValue(props.item.staticSigma)}
            </Typography>
          )}
        </Box>
        <Box className="row-cell">
          {!props.hiddenFields.includes("mu") && (
            <Typography variant="par02Regular">
              {formatValue(props.item.dynamicMu)}
            </Typography>
          )}
          {!props.hiddenFields.includes("sigma") && (
            <Typography variant="par02Regular">
              {formatValue(props.item.dynamicSigma)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export interface SimulationModelsHistoryTableProps {
  sx?: BoxProps["sx"];
  items: SimulationModelsHistoryItem[];
  hiddenFields: string[];
}

export const SimulationModelsHistoryTable = ({
  sx,
  items,
  hiddenFields,
}: SimulationModelsHistoryTableProps) => {
  return (
    <Box
      sx={{
        display: "grid",
        ".MuiTypography-root": {
          color: "table.text",
        },
        "& > *:first-of-type > *:first-of-type": {
          borderTopLeftRadius: "5px",
        },
        "& > *:first-of-type > *:last-of-type": {
          borderTopRightRadius: "5px",
        },
        "& > *:last-of-type > * > *:first-of-type": {
          borderBottomLeftRadius: "5px",
        },
        "& > *:last-of-type > * > *:last-of-type": {
          borderBottomRightRadius: "5px",
        },
        "& > *:nth-of-type(even)": {
          backgroundColor: "table.background.even",
        },
        "& > *:nth-of-type(odd)": {
          backgroundColor: "table.background.odd",
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: getGridTemplateColumns(2),
          position: "sticky",
          top: 0,
          backgroundColor: "table.header",
          zIndex: 10,
        }}
      >
        <LabelHeader />
        <ColumnHeader key="static" label="Static" hiddenFields={hiddenFields} />
        <ColumnHeader
          key="dynamic"
          label="Dynamic"
          hiddenFields={hiddenFields}
        />
      </Box>
      {items.map((i) => (
        <Row key={i.date} item={i} hiddenFields={hiddenFields} />
      ))}
    </Box>
  );
};
